import { formatDate, ReportUrl, formatNumber } from "@common";
import { Fieldset, LongNameOptionComponent } from "@components";
import { CalendarToolForTimeOrganization, Triangle } from "@icons";
import {
    AppState,
    ReportCampaignListItem,
    reportCampaignListSelector,
    reportParametersForm,
    reportParametersFormBindings,
    campaignDateRangeSelector,
    CampaignInfo,
    CurrencyMapping
} from "@store";
import * as cn from "classnames";
import * as React from "react";
import DayPickerInput from "react-day-picker/DayPickerInput";
import { connect, Dispatch } from "react-redux";
import { Link } from "react-router-dom";
import { FetchingState } from "rd-redux-http";
import "react-day-picker/lib/style.css";

export interface ReportsParametersFormComponentProps {
    campaignList: ReportCampaignListItem[];
    formData: typeof reportParametersForm.types.selectorResult;
    formEvents: typeof reportParametersForm.types.eventBindings;
    selectedCampaign: CampaignInfo;
    currencies: CurrencyMapping[];
}

class ReportsParametersFormComponent extends React.Component<ReportsParametersFormComponentProps> {
    constructor(props: ReportsParametersFormComponentProps) {
        super(props);
        this.state = {
            dateSelector: "over"
        };
    }

    render() {
        const { formData: form, formEvents: events, campaignList, selectedCampaign, currencies } = this.props;
        const campaignCurrency = this.props.currencies && currencies.find(c => c.isoCode === selectedCampaign.currency);

        const modifiers = {
            fromDate: new Date(this.props.selectedCampaign.startDate),
            toDate: new Date(this.props.selectedCampaign.endDate)
        };

        return (
            <form {...events.form}>
                <section className="filters">
                    <Fieldset
                        className="report-aside-fieldset-select"
                        label="Campaign"
                        errorMessage={form.fields.campaignId}
                        icon={Triangle}
                        svgClassName="svg-triangle"
                        isMandatory={true}
                    >
                        <select
                            className="common-select"
                            name="campaign"
                            id="campaign"
                            value={form.fields.campaignId.value || "default"}
                            {...events.fields.campaignId}
                            title={campaignList.find(p => p.campaignId === form.fields.campaignId.value).name}
                        >
                            <option value="default" disabled>
                                Select campaign...
                            </option>
                            {campaignList.map(p => (
                                <LongNameOptionComponent
                                    key={`ReportParameterForm-CampaignItem-${p.campaignId}`}
                                    value={p.campaignId}
                                    name={p.name}
                                />
                            ))}
                        </select>
                    </Fieldset>
                    {selectedCampaign &&
                        selectedCampaign.status !== "draft" &&
                        selectedCampaign.status !== "pending" && (
                            <>
                                <span className="reports-info_campaign-info-block">
                                    <span className="reports-info-main-subtitle">{selectedCampaign.campaignId}</span>
                                    <span className="reports-info-status">
                                        <span className={cn("status-circles ", selectedCampaign.status + "-circle")} />
                                        <span className="status-label">{selectedCampaign.status}</span>
                                    </span>
                                </span>
                                <span className="reports-info_block">
                                    <p className="reports-info_result-title">Price model</p>
                                    <p className="reports-info_result-value">{selectedCampaign.priceModel}</p>
                                </span>
                                <span className="reports-info_block reports-info_block_last">
                                    <p className="reports-info_result-title">Full budget</p>
                                    <p className="reports-info_result-value">
                                        {campaignCurrency ? (
                                            <>
                                                {formatNumber(selectedCampaign.budget)}
                                                <span className="result-value_symbol">{campaignCurrency.symbol}</span>
                                            </>
                                        ) : (
                                            formatNumber(selectedCampaign.budget)
                                        )}
                                    </p>
                                </span>
                            </>
                        )}
                    <Fieldset
                        className="Fieldset__date report-aside-fieldset-date fix-index-fieldset"
                        label="From"
                        errorMessage={form.fields.fromDate}
                        icon={CalendarToolForTimeOrganization}
                        isBadDateSvg={true}
                        isMandatory={true}
                    >
                        <DayPickerInput
                            value={form.fields.fromDate.value || ""}
                            onDayChange={(day: any) => {
                                events.fields.fromDate.onChange(formatDate(day));
                            }}
                            placeholder="YYYY-MM-DD"
                            format="YYYY-MM-DD"
                            formatDate={formatDate}
                            dayPickerProps={{
                                disabledDays: {
                                    before: new Date(this.props.selectedCampaign.startDate),
                                    after: new Date(this.props.selectedCampaign.endDate)
                                },
                                modifiers
                            }}
                            inputProps={{
                                readOnly: true
                            }}
                        />
                    </Fieldset>
                    <Fieldset
                        className="Fieldset__date report-aside-fieldset-date"
                        label="To"
                        errorMessage={form.fields.toDate}
                        icon={CalendarToolForTimeOrganization}
                        isBadDateSvg={true}
                        isMandatory={true}
                    >
                        <DayPickerInput
                            value={form.fields.toDate.value || ""}
                            onDayChange={(day: any) => {
                                events.fields.toDate.onChange(formatDate(day));
                            }}
                            placeholder="YYYY-MM-DD"
                            format="YYYY-MM-DD"
                            formatDate={formatDate}
                            dayPickerProps={{
                                disabledDays: {
                                    before: new Date(this.props.selectedCampaign.startDate),
                                    after: new Date(this.props.selectedCampaign.endDate)
                                },
                                modifiers
                            }}
                            inputProps={{
                                readOnly: true
                            }}
                        />
                    </Fieldset>
                </section>
                {this.renderDateSelector()}
            </form>
        );
    }

    private renderDateSelector() {
        const urlMatch = ReportUrl.match(window.location.toString(), true);
        const { campaignList } = this.props;
        const { fromDate, toDate } = urlMatch.isMatched
            ? urlMatch.query || { fromDate: "", toDate: "" }
            : { fromDate: "", toDate: "" };

        [
            { title: "Last Week", range: "last-week" },
            { title: "Last Month", range: "last-month" },
            { title: "Overall", range: "overall" }
        ].map(template => {
            const campaignId = urlMatch.isMatched ? urlMatch.params.campaignId : undefined;

            const campaign = campaignList.find(c => c.campaignId === campaignId);
            const dates = campaignDateRangeSelector(campaign, template.range as any);

            return ReportUrl.replace(
                window.location.toString(),
                {},
                {
                    fromDate: dates.fromDate,
                    toDate: dates.toDate
                }
            );
        });

        return (
            <section className="date-selector">
                <p className="date-selector-title">Quick selection</p>
                <div className="date-selector-switch-row">
                    {urlMatch.isMatched &&
                        [
                            { title: "Last Week", range: "last-week" },
                            { title: "Last Month", range: "last-month" },
                            { title: "Overall", range: "overall" }
                        ].map(template => {
                            const campaignId = urlMatch.isMatched ? urlMatch.params.campaignId : undefined;

                            const campaign = campaignList.find(c => c.campaignId === campaignId);
                            const dates = campaignDateRangeSelector(campaign, template.range as any);

                            return (
                                <Link
                                    className={cn("date-selector-switch", {
                                        "date-selector-switch-active":
                                            dates.fromDate === fromDate && dates.toDate === toDate
                                    })}
                                    key={template.range}
                                    to={ReportUrl.replace(
                                        window.location.toString(),
                                        {},
                                        {
                                            fromDate: dates.fromDate,
                                            toDate: dates.toDate
                                        }
                                    )}
                                >
                                    {template.title}
                                </Link>
                            );
                        })}
                </div>
            </section>
        );
    }
}

export interface ReportsParametersFormProps {}

export const ReportsParametersForm = connect(
    (state: AppState): Partial<ReportsParametersFormComponentProps> => {
        const urlMatch = ReportUrl.match(window.location.toString(), true);
        const initialData = urlMatch.isMatched ? { ...(urlMatch.query || {}), ...urlMatch.params } : {};
        const selectedCampaign =
            FetchingState.isSuccess(state.campaignList.campaigns) &&
            urlMatch.isMatched &&
            state.campaignList.campaigns.data.filter(campaign => campaign.campaignId === urlMatch.params.campaignId)[0];

        let campaignDates: { fromDate: string; toDate: string };

        if (selectedCampaign) {
            campaignDates = {
                fromDate: formatDate(selectedCampaign.startDate),
                toDate: formatDate(selectedCampaign.endDate)
            };
        }

        return {
            formData: reportParametersForm.selector(state.reports.form, initialData, campaignDates),
            campaignList: reportCampaignListSelector(state),
            selectedCampaign,
            currencies: FetchingState.isSuccess(state.mappings.currencies) && state.mappings.currencies.data
        };
    },
    (dispatch: Dispatch): Partial<ReportsParametersFormProps> => ({
        formEvents: reportParametersFormBindings.bind(dispatch, undefined)
    })
)(ReportsParametersFormComponent);
